import { useAppDispatch } from "@app/hooks"
import {
  CourseClassRegistrationClient,
  CourseRegistrationEndStatus,
} from "@clients/courseClassRegistrationClient"
import { ProgramManualUnlockClient } from "@clients/ProramManualUnlockClient"
import { UsersClient } from "@clients/usersClient"
import OptionsButton2 from "@cmp/buttons/optionsButton2"
import { fetchCourseClasses } from "@features/courseInstance/courseInstanceSlice"
import { showSnackbarItem, SnackbarDuration } from "@features/ui/uiSlice"
import { Stack, SvgIcon, Typography } from "@mui/material"
import AppIcon from "@utils/appIcon"
import appIcons from "@utils/appIcons"

export function CourseInstanceStudentsActionMenu({
  courseInstanceId,
  courseClassId,
  studentId,
  studentEmail,
  operatingUserClientId,
}: {
  courseInstanceId: string
  courseClassId: string
  studentId: string
  studentEmail: string
  operatingUserClientId: string
}) {
  const dispatch = useAppDispatch()

  // Copy a faithful impersonation link for this student (campus or LMS),
  // matching the program > students action menu so both student lists behave
  // the same.
  const copyImpersonationLink = (target: "campus" | "lms") => {
    if (!studentEmail) {
      return
    }
    UsersClient.generateImpersonationLink(studentEmail, target)
      .then((link) => {
        navigator.clipboard.writeText(link)
        dispatch(
          showSnackbarItem({
            message: `${
              target === "lms" ? "LMS" : "Campus"
            } link copied. Open the URL in incognito mode.`,
            type: "success",
            duration: SnackbarDuration.short,
          }),
        )
      })
      .catch(() =>
        dispatch(
          showSnackbarItem({
            message: "Failed to copy impersonation link, try again later.",
            type: "error",
            duration: SnackbarDuration.short,
          }),
        ),
      )
  }
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        height: "100%",
        mr: "22px",
      }}
    >
      <OptionsButton2
        buttonModel={{
          type: "icon",
          props: {
            sx: {
              marginLeft: "auto",
              display: "flex",
            },
            size: "small",
            children: (
              <SvgIcon
                component={appIcons.dotsPretty}
                inheritViewBox
                sx={{ color: "primary.main" }}
              />
            ),
          },
        }}
        items={[
          {
            type: "menu-item",
            props: {
              key: "impersonate",
              children: (
                <Stack direction="row" gap="8px" alignItems="center">
                  <AppIcon icon={appIcons.eye} />
                  <Typography variant="body2">
                    Copy impersonation link
                  </Typography>
                </Stack>
              ),
              onClick: () => copyImpersonationLink("campus"),
            },
          },
          {
            type: "menu-item",
            props: {
              key: "impersonate-lms",
              children: (
                <Stack direction="row" gap="8px" alignItems="center">
                  <AppIcon icon={appIcons.eye} />
                  <Typography variant="body2">
                    Copy LMS impersonation link
                  </Typography>
                </Stack>
              ),
              onClick: () => copyImpersonationLink("lms"),
            },
          },
          {
            type: "menu-item",
            props: {
              key: "remove-student",
              children: (
                <Stack direction="row" gap="8px" alignItems="center">
                  <AppIcon icon={appIcons.xCircle} />
                  <Typography variant="body2">Remove Student</Typography>
                </Stack>
              ),
              onClick: () => {
                CourseClassRegistrationClient.unregisterStudentFromClass(
                  courseClassId,
                  studentId,
                  new Date(),
                  {
                    status: CourseRegistrationEndStatus.Removed,
                    removedBy: operatingUserClientId,
                  },
                ).then(() => dispatch(fetchCourseClasses()))
              },
            },
          },
          {
            type: "menu-item",
            props: {
              key: "unlock next week",
              children: (
                <Stack direction="row" gap="8px" alignItems="center">
                  <AppIcon icon={appIcons.lockUnlocked01} />
                  <Typography variant="body2">
                    Unlock next content week
                  </Typography>
                </Stack>
              ),
              onClick: () =>
                ProgramManualUnlockClient.unlockCourseNextWeek(
                  studentId,
                  courseInstanceId,
                )
                  .then(() =>
                    dispatch(
                      showSnackbarItem({
                        message: "Unlocked next week",
                        type: "success",
                        duration: SnackbarDuration.short,
                      }),
                    ),
                  )
                  .catch(() =>
                    dispatch(
                      showSnackbarItem({
                        message: "Failed to unlock next week",
                        type: "error",
                        duration: SnackbarDuration.short,
                      }),
                    ),
                  ),
            },
          },
        ]}
      />
    </Stack>
  )
}
